import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import Layout from "@/components/Layout";
import { useNavigate, useParams } from "react-router-dom";
import AutoEditor from "@/components/autoeditor";
import { toast } from "sonner";
import { getClipStatusText } from "@/lib/get-clip-status-text";
import { useClipPolling } from "@/hooks/use-polling";

const ClipEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate()
  const { data: clip, loading, error } = useClipPolling(id, {
    enabled: !!id,
    interval: 3000, // Poll every 3 seconds
  });

  // Utility function to format ETA seconds
  const formatEta = (etaSeconds: number | null | undefined): string => {
    if (!etaSeconds || etaSeconds <= 0) return "";

    const minutes = Math.floor(etaSeconds / 60);
    const seconds = Math.round(etaSeconds % 60);

    if (minutes > 0 && seconds > 0) {
      return `${t('clipEdit.estimate')} ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${t('clipEdit.estimate')} ${minutes}m`;
    } else {
      return `${t('clipEdit.estimate')} ${seconds}s`;
    }
  };

  useEffect(() => {
    if (!clip) {
      return;
    }

    if (
      clip?.rendering_status !== 'success' &&
      clip?.rendering_status !== 'skipped' &&
      clip?.rendering_status !== 'failure'
    ) {
      navigate(`/clips`)
      toast.error(t('clipEdit.waitForGeneration'))
    }
  }, [clip?.preparing_status, clip?.rendering_status, clip?.clipping_status, navigate, t])

  // Initial loading state while fetching clip
  if (loading && !clip) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-2">
            <div className="h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto" />
            <p className="text-sm text-muted-foreground">{t('clipEdit.loadingClip')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-2">
            <p className="text-sm text-destructive">{t('clipEdit.errorLoading')}</p>
            <p className="text-xs text-muted-foreground">{error.message}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!clip) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-sm text-muted-foreground">{t('clipEdit.clipNotFound')}</p>
        </div>
      </Layout>
    );
  }

  // Check if clip is expired
  if (
    clip.clipping_status === 'expired' ||
    clip.preparing_status === 'expired' ||
    clip.rendering_status === 'expired'
  ) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-sm text-muted-foreground">{t('clipEdit.clipExpired')}</p>
        </div>
      </Layout>
    );
  }

  // Show loading state based on preparing_status
  if (clip.preparing_status && clip.preparing_status !== "success" && clip.preparing_status !== "skipped") {
    const statusText = getClipStatusText(clip);
    const etaText = formatEta(clip.eta_seconds);

    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-2">
            <div className="h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto" />
            <p className="text-sm font-medium">{t('clipEdit.preparingClip')}</p>
            <p className="text-xs text-muted-foreground">{statusText}</p>
            {etaText && (
              <p className="text-xs text-muted-foreground">{etaText}</p>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-background" style={{
        minHeight: 'calc(100vh - 180px)',
      }}>
        <div className="w-full space-y-4 h-full">
          <AutoEditor clip={clip} />
        </div>
      </div>
    </Layout>
  );
};

export default ClipEdit;
